import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listTickets, createTicket, updateTicket, getTicketStats } from '../lib/api';
import type { TicketStats } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import type { Ticket } from '../types';

export function useTickets(
  projectId: string | undefined,
  params?: { status?: string; type?: string }
) {
  return useQuery({
    queryKey: queryKeys.tickets(projectId ?? '', params),
    queryFn: () => listTickets(projectId!, params),
    enabled: !!projectId,
  });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: string;
      data: {
        title: string;
        description?: string;
        status?: string;
        type?: string;
        assigneeId?: string;
      };
    }) => createTicket(projectId, data),
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.tickets(projectId) });
    },
  });
}

export function useUpdateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Ticket> }) =>
      updateTicket(id, data),
    onSuccess: (data) => {
      void qc.invalidateQueries({
        queryKey: queryKeys.tickets(data.projectId),
      });
    },
  });
}

export function useTicketStats(projectId: string | undefined) {
  return useQuery<TicketStats>({
    queryKey: ['ticketStats', projectId],
    queryFn: () => getTicketStats(projectId!),
    enabled: !!projectId,
  });
}

export function useSummarizeTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, projectId }: { id: string; projectId: string }) =>
      updateTicket(id, { summaryIsStale: false } as Partial<Ticket>).then(
        () => ({ id, projectId })
      ),
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.tickets(projectId) });
    },
  });
}
